import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useToast } from '../components/ToastContainer';
import useAuthStore from '../stores/useAuthStore';
import useCourseStore from '../stores/useCourseStore';
import axiosClient from '../services/api';

const InstructorAssignmentDashboard = () => {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const toast = useToast();
    const {
        enrolledCourses,
        fetchMyCourses,
        fetchISACourse,
        isLoading: isCourseLoading
    } = useCourseStore();

    const [view, setView] = useState('courses');
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [submissions, setSubmissions] = useState([]);

    useEffect(() => {
        if (view === 'courses') {
            fetchMyCourses();
        }
    }, [view, fetchMyCourses]);

    const loadSubmissionsForCourse = async (course) => {
        setSelectedCourse(course);
        setView('assignments');
        setIsLoading(true);

        try {
            const fullCourse = await fetchISACourse(course._id);
            if (!fullCourse) {
                setIsLoading(false);
                return;
            }

            const assignmentIds = fullCourse.modules
                ?.flatMap(m => m.lessons)
                ?.filter(l => l.assignment)
                ?.map(l => (typeof l.assignment === 'object' ? l.assignment._id : l.assignment))
                .filter(Boolean) || [];

            if (assignmentIds.length === 0) {
                setSubmissions([]);
                setIsLoading(false);
                return;
            }

            const submissionPromises = assignmentIds.map(async (id) => {
                try {
                    const assignmentRes = await axiosClient.get(`/learning/assignments/${id}`);
                    const assignment = assignmentRes.data?.assignment;

                    const submissionsRes = await axiosClient.get(`/learning/assignments/${id}/submissions`);
                    const subs = submissionsRes.data?.success ? submissionsRes.data.data : [];

                    return subs.map(sub => ({
                        ...sub,
                        assignment: {
                            _id: assignment?._id,
                            title: assignment?.title,
                            points: assignment?.points || 100
                        }
                    }));
                } catch (err) {
                    return [];
                }
            });

            const allSubmissions = (await Promise.all(submissionPromises)).flat();
            setSubmissions(allSubmissions);
        } catch (error) {
            setSubmissions([]);
        } finally {
            setIsLoading(false);
        }
    };

    const groupedSubmissions = submissions.reduce((acc, sub) => {
        const id = sub.assignment?._id?.toString() || 'unknown';
        if (!acc[id]) acc[id] = [];
        acc[id].push(sub);
        return acc;
    }, {});

    const assignmentStats = Object.entries(groupedSubmissions).map(([assignmentId, subs]) => {
        const total = subs.length;
        const graded = subs.filter(s => s.gradedAt).length;
        const passThreshold = (subs[0]?.assignment?.points || 100) * 0.5;
        const passed = subs.filter(s => s.grade !== undefined && s.grade >= passThreshold).length;
        const passRate = total > 0 ? Math.round((passed / total) * 100) : 0;
        const avgGrade = total > 0 ? (subs.reduce((sum, s) => sum + (s.grade || 0), 0) / total).toFixed(1) : 0;

        return {
            assignmentId,
            assignmentTitle: subs[0]?.assignment?.title || 'Untitled Assignment',
            total,
            graded,
            passed,
            passRate,
            avgGrade,
            submissions: subs
        };
    });


    return (
        <DashboardLayout userRole="instructor">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-6">
                    <div className='mb-6'>
                        <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">
                            {view === 'courses' ? 'Assignment Dashboard' : `${selectedCourse?.title} Submissions`}
                        </h1>

                    </div>
                    <div className='flex justify-between'>
                        <p className="text-neutral-600 dark:text-neutral-400 mt-2">
                            {view === 'courses'
                                ? 'Track and grade student assignment submissions'
                                : 'Review and grade submissions for this course'}
                        </p>
                        {view === 'assignments' && (
                            <button
                                onClick={() => setView('courses')}
                                className="px-4 py-2 text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                            >
                                ← Back to Courses
                            </button>
                        )}
                    </div>

                </div>

                {view === 'courses' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {isCourseLoading ? (
                            <div className="col-span-full flex justify-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                            </div>
                        ) : enrolledCourses.length > 0 ? (
                            enrolledCourses.map(course => {
                                const assignmentCount = course.modules
                                    ?.flatMap(m => m.lessons)
                                    ?.filter(l => l.assignment)?.length || 0;

                                return (
                                    <div
                                        key={course._id}
                                        className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6 cursor-pointer hover:shadow-md transition-shadow"
                                        onClick={() => loadSubmissionsForCourse(course)}
                                    >
                                        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-3">{course.title}</h2>
                                        <div className="space-y-3">
                                            <div className="flex justify-between">
                                                <span className="text-sm text-neutral-600 dark:text-neutral-400">Assignments</span>
                                                <span className="font-medium text-neutral-900 dark:text-white">{assignmentCount}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-sm text-neutral-600 dark:text-neutral-400">Status</span>
                                                <span className="font-medium text-neutral-900 dark:text-white">
                                                    {assignmentCount > 0 ? 'Active' : 'No assignments'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="col-span-full text-center py-12">
                                <div className="text-6xl mb-4">📝</div>
                                <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">No courses found</h3>
                                <p className="text-neutral-600 dark:text-neutral-400">
                                    Create your first course to start managing assignments.
                                </p>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-8">
                        {isLoading ? (
                            <div className="flex justify-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                            </div>
                        ) : assignmentStats.length > 0 ? (
                            assignmentStats.map(stat => (
                                <div key={stat.assignmentId} className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h3 className="text-lg font-medium text-neutral-900 dark:text-white">
                                                {stat.assignmentTitle}
                                            </h3>
                                            <p className="text-sm text-neutral-500 dark:text-neutral-400">
                                                {stat.total} submissions • {stat.graded} graded
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Pass Rate</p>
                                            <p className={`text-lg font-bold ${stat.passRate >= 70 ? 'text-success-600' : 'text-warning-600'}`}>
                                                {stat.passRate}%
                                            </p>
                                            <p className="text-sm text-neutral-500 dark:text-neutral-400">Avg Grade: {stat.avgGrade}</p>
                                        </div>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
                                            <thead className="bg-neutral-50 dark:bg-neutral-700/50">
                                                <tr>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Student</th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Submitted</th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Grade</th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Status</th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
                                                {stat.submissions.map(sub => (
                                                    <tr key={sub._id}>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm font-medium text-neutral-900 dark:text-white">
                                                                {sub.student?.name || 'N/A'}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                                                            {sub.submittedAt ? new Date(sub.submittedAt).toLocaleDateString() : 'N/A'}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            {sub.gradedAt ? (
                                                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-300">
                                                                    {sub.grade}
                                                                </span>
                                                            ) : (
                                                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-300">
                                                                    Pending
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                                                            {sub.gradedAt ? 'Graded' : 'Submitted'}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                            <button
                                                                onClick={() => navigate(`/instructor/grade/${sub._id}`)}
                                                                className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                                                            >
                                                                {sub.gradedAt ? 'View' : 'Grade'}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-12">
                                <div className="text-6xl mb-4">📭</div>
                                <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">No assignments with submissions</h3>
                                <p className="text-neutral-600 dark:text-neutral-400">
                                    Students will appear here once they submit.
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default InstructorAssignmentDashboard;